import R from 'ramda';

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { push } from 'redux-first-history'; // If you are using connected-react-router
import { Store as RNC } from 'react-notifications-component';

import { postRegister, postLogin, postLogout } from '_api/auth';

import { getUser } from '_api/user';

import { dispatchError } from '_utils/api';
import { handleSuccess } from '../../utils/api';

const initialState = {
    isAuthenticated: false,
    user: null,
    loading: false,
    error: null,
};

export const attemptGetUser = createAsyncThunk(
    'auth/get_user',
    async (_, thunkAPI) => {
        try {
            const response = await getUser();

            return { user: response.user };
        } catch (error) {
            return thunkAPI.rejectWithValue(error.body || 'Get user failed');
        }
    }
)

export const register = createAsyncThunk(
    'auth/register',
    async (newUser, thunkAPI) => {
        try {
            const response = await postRegister(newUser);

            return { user: response.user };
        } catch (err) {
            dispatchError(dispatch)(err)

            let errorPayload;

            if (err.body) {
                errorPayload = {
                    status: err.status,
                    message: err.body.message
                }
            } else if (err.req) {
                errorPayload = {
                    status: 'no_response',
                    messsage: 'No response recieved from server'
                }
            } else {
                errorPayload = {
                    status: 'unknown_error',
                    message: err.body?.message || "An unkonwn error occurered"
                }
            }

            return thunkAPI.rejectWithValue(errorPayload);
        }
    }
);

export const login = createAsyncThunk(
    'auth/login',
    async (creds, { dispatch, rejectWithValue }) => {
        try {
            const response = await postLogin(creds);
            dispatch(attemptGetUser());
            dispatch(push('/'));
            return { user: response.user };
        } catch (err) {
            dispatchError(dispatch)(err)

            let errorPayload;

            if (err.body) {
                errorPayload = {
                    status: err.status,
                    message: err.body.message
                }
            } else if (err.req) {
                errorPayload = {
                    status: 'no_response',
                    messsage: 'No response recieved from server'
                }
            } else {
                errorPayload = {
                    status: 'unknown_error',
                    message: err.body?.message || "An unkonwn error occurered"
                }
            }

            return rejectWithValue(errorPayload);
        }
    }
)

export const logout = createAsyncThunk(
    'auth/logout',
    async (_, thunkAPI) => {
        try {
            const response = await postLogout();
            return response.body;
        } catch (err) {
            dispatchError(dispatch)(err)

            let errorPayload;

            if (err.body) {
                errorPayload = {
                    status: err.status,
                    message: err.body.message
                }
            } else if (err.req) {
                errorPayload = {
                    status: 'no_response',
                    messsage: 'No response recieved from server'
                }
            } else {
                errorPayload = {
                    status: 'unknown_error',
                    message: err.body?.message || "An unkonwn error occurered"
                }
            }

            return rejectWithValue(errorPayload);
        }
    }
)

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginSuccess(state, action) {
            state.isAuthenticated = true;
            state.user = action.payload;
        },
        logoutSuccess(state) {
            state.isAuthenticated = false;
            state.user = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(attemptGetUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(attemptGetUser.fulfilled, (state, action) => {
                state.loading = false;
                state.isAuthenticated = !R.isEmpty(action.payload.user);
                state.user = action.payload.user;
            })
            .addCase(attemptGetUser.rejected, (state, action) => {
                if (action.payload) {
                    state.error = action.payload.message;
                } else {
                    state.error = action.error.message;
                }
                state.loading = false;
                state.isAuthenticated = false;
                state.user = null;
            })
            .addCase(register.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(register.fulfilled, (state, action) => {
                state.loading = false;
                state.isAuthenticated = true;
                state.user = action.payload.user;
            })
            .addCase(register.rejected, (state, action) => {
                if (action.payload) {
                    state.error = action.payload.message;
                } else {
                    state.error = action.error.message;
                }
                state.loading = false;
                state.isAuthenticated = false;
                state.user = null;
            })
            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loading = false;
                state.isAuthenticated = true;
                state.user = action.payload.user;
            })
            .addCase(login.rejected, (state, action) => {
                state.error = action.payload.message;
                state.loading = false;
                state.isAuthenticated = false;
                state.user = null;
            })
            .addCase(logout.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(logout.fulfilled, (state) => {
                state.loading = false;
                state.isAuthenticated = false;
                state.user = null;
                state.error = null;
            })
            .addCase(logout.rejected, (state, action) => {
                if (action.payload) {
                    state.error = action.payload.message;
                } else {
                    state.error = action.error.message;
                }
                state.loading = false;
                state.isAuthenticated = false;
                state.user = null;
            });
    },
});


export const { loginSuccess, logoutSuccess } = authSlice.actions;
export default authSlice.reducer;